import { useRef } from 'react'
import cc from 'classcat'

import { EditorLink } from '../../EditorLink/EditorLink'
import { imageCategories } from '../UnsplashImageSearch.helpers'
import { useTranslate, withComponents } from '../../../utils/translate'
import { useUnsplash } from '../UnsplashImageSearchContext'

export function UnsplashImageSearchHeader() {
  const t = useTranslate('interface', 'components.imageUploadSelectionComponent.UnsplashImageSearchPopover')
  const { searchTerm, isLoading, activeCategory, isDownloading, setSearchTerm, searchUnsplash, handleCategoryClick } =
    useUnsplash()
  const categoryRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const handleCategoryButtonClick = (category: string) => {
    handleCategoryClick(category)

    setTimeout(() => {
      // Scroll category button into view
      // randomly trying solutions, this comment worked https://stackoverflow.com/a/71089401
      if (categoryRefs.current[category]) {
        categoryRefs.current[category]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
      // fixes Chrome scrollIntoView issue
    }, 10)
  }

  return (
    <div className="unsplash-search-header">
      <h3 className="unsplash-search-header-title">{t('title')}</h3>
      <p>
        {withComponents(t)('description', {
          link: (
            <EditorLink href={t('linkHref')} type="external">
              {t('linkText')}
            </EditorLink>
          ),
        })}
      </p>

      <div className="unsplash-search-header-input">
        <input
          type="text"
          className="unsplash-search-header-input-field"
          placeholder={t('searchInputField.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && searchUnsplash(searchTerm)}
          disabled={isLoading || isDownloading}
        />
        <button
          className="unsplash-search-header-input-button"
          onClick={() => searchUnsplash(searchTerm)}
          disabled={isLoading || !searchTerm.trim() || isDownloading}
          title={t('searchInputField.searchButton.tooltip')}
          aria-label={t('searchInputField.searchButton.accessibilityLabel')}
        />
      </div>

      <div className="unsplash-search-categories">
        <scroll-shadow>
          <div className="unsplash-search-categories-scroll">
            {imageCategories.map((category) => (
              <button
                key={category.searchTerm}
                ref={(element) => {
                  categoryRefs.current[category.searchTerm] = element
                }}
                className={cc([
                  'unsplash-search-category-chip',
                  activeCategory === category.searchTerm && 'unsplash-search-category-chip-active',
                  isDownloading && 'unsplash-search-category-chip-disabled',
                ])}
                onClick={() => handleCategoryButtonClick(category.searchTerm)}
                disabled={activeCategory === category.searchTerm || isDownloading}
              >
                {t(`imageCategorySuggestions.${category.l10nKey}`)}
              </button>
            ))}
          </div>
        </scroll-shadow>
      </div>
    </div>
  )
}
